import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { designer } from "../data/portfolio";

const links = [
  { label: "Work", href: "#work" },
  { label: "About", href: "#about" },
  { label: "Words", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    // href looks like "#work" or "#top"
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-md bg-[#FFFFFF]/75 border-b border-[#E4E6EC]/60" : "bg-transparent"
        }`}
        data-testid="site-navigation"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-5 flex items-center justify-between">
          <a
            href="#top"
            onClick={(e) => handleNavClick(e, "#top")}
            className="flex items-baseline gap-2 group"
            data-testid="nav-logo"
          >
            <span className="font-display text-2xl tracking-tight text-[#0E1A2B]">{designer.name}</span>
            <span className="hidden md:inline text-[10px] tracking-[0.28em] uppercase text-[#6E7583]">/ Studio</span>
          </a>

          <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.href)}
                className="text-sm tracking-wide text-[#0E1A2B] link-underline"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, "#contact")}
              className="text-sm tracking-wide px-5 py-2.5 rounded-full bg-[#0E1A2B] text-[#FFFFFF] hover:bg-[#E5326A] transition-colors duration-500"
              data-testid="nav-cta"
            >
              Say hello
            </a>
          </nav>

          <button
            aria-label="Open menu"
            className="md:hidden p-2 rounded-full border border-[#E4E6EC] bg-[#FFFFFF]/70"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
          >
            <Menu size={18} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-50 bg-[#FFFFFF]"
            data-testid="nav-mobile-panel"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="font-display text-2xl tracking-tight">{designer.name}</span>
              <button
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="p-2 rounded-full border border-[#E4E6EC]"
                data-testid="nav-menu-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pt-16 flex flex-col gap-8">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleNavClick(e, l.href)}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5 }}
                  className="font-display text-5xl tracking-tight text-[#0E1A2B]"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
